import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Service, inject, signal } from '@angular/core';
import { Observable, Subscription, map, of, switchMap, tap, timer } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_AUTH_TOKEN } from '../../../core/http/http-context-tokens';

const RECURSO = `${environment.apiUrl}/sai/publico/desafio-formulario`;

/** O cabeçalho em que o token volta no `POST` de abertura. */
export const CABECALHO_DESAFIO = 'X-Desafio-Formulario';

/** O cabeçalho com que a API marca o 422 do desafio — e o distingue dos 422 de negócio. */
export const CABECALHO_DESAFIO_INVALIDO = 'X-Desafio-Invalido';

/** O nome do campo invisível. Neutro de propósito: `url`/`website` o autocompletar preencheria. */
export const CAMPO_HONEYPOT = 'desSite';

/** O que a tela mostra quando a API recusa o desafio: o cidadão só precisa clicar de novo. */
export const MENSAGEM_DESAFIO_RECUSADO = 'Não foi possível validar o envio. Tente enviar novamente.';

/** Folga sobre a idade mínima: o relógio do navegador começa a contar depois do servidor. */
const FOLGA_MS = 500;

/** Token a menos disto do vencimento é trocado antes do envio, em vez de ir e voltar com 422. */
const MARGEM_VENCIMENTO_MS = 60_000;

export interface DesafioFormulario {
  desafio: string;
  validadeSegundos: number;
  idadeMinimaSegundos: number;
}

/**
 * O **desafio de formulário** das três aberturas públicas (change anti-abuso-superficie-publica):
 * um token assinado pela API que o portal pede ao abrir o formulário e devolve no cabeçalho
 * `X-Desafio-Formulario` do `POST`. Sem cache — cada formulário pede o seu —, sem token de
 * sessão — o desafio é igual para anônimo e identificado — e com o erro local: quem pede decide
 * o que fazer quando falha.
 */
@Service()
export class DesafioFormularioService {
  private readonly http = inject(HttpClient);

  obter(): Observable<DesafioFormulario> {
    const contexto = new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true).set(SKIP_AUTH_TOKEN, true);

    return this.http
      .get<ResponseApi<DesafioFormulario>>(RECURSO, { context: contexto })
      .pipe(map((resposta) => resposta.data));
  }
}

/** O 422 da recusa do desafio — pelo cabeçalho, nunca pelo texto. */
export function ehRecusaDoDesafio(erro: unknown): boolean {
  return (
    erro instanceof HttpErrorResponse &&
    erro.status === 422 &&
    erro.headers?.get(CABECALHO_DESAFIO_INVALIDO) === 'true'
  );
}

/**
 * O desafio de **um** formulário, guardado no store do fluxo: o token, o instante em que chegou e
 * o campo honeypot. Os três stores (manifestação, pedido e-SIC e solicitação de serviço) têm um.
 *
 * - `carregar()` na entrada do formulário — o token envelhece enquanto o cidadão preenche;
 * - `paraEnvio()` no envio: devolve o token guardado ou, se a carga falhou ou ele está para
 *   vencer, pede outro — e **espera** o que faltar para a idade mínima, que a API anuncia: um
 *   token novo demais levaria 422;
 * - `renovar()` depois do 422 do desafio; `descartar()` no cancelar e no recomeçar.
 */
export class DesafioDoFormulario {
  readonly token = signal<string | null>(null);

  /** O campo invisível. Fica vazio para quem usa a tela; o robô que lê o HTML o preenche. */
  readonly honeypot = signal('');

  private obtidoEm = 0;
  private idadeMinimaMs = 0;
  private validadeMs = 0;
  private pedido?: Subscription;

  constructor(
    private readonly servico: DesafioFormularioService,
    private readonly agora: () => number = () => Date.now(),
  ) {}

  /** Pede o token se ainda não há um. Falhar aqui não é erro: o envio tenta de novo. */
  carregar(): void {
    if (this.token() || this.pedido) return;

    this.pedido = this.servico.obter().subscribe({
      next: (desafio) => this.guardar(desafio),
      error: () => (this.pedido = undefined),
      complete: () => (this.pedido = undefined),
    });
  }

  /** Troca o token — o 422 do desafio pede um novo, e os dados do formulário ficam. */
  renovar(): void {
    this.esquecerToken();
    this.carregar();
  }

  /** Esquece token e honeypot. O próximo `carregar()` pede outro. */
  descartar(): void {
    this.esquecerToken();
    this.honeypot.set('');
  }

  /**
   * O token do envio. Erro aqui (o GET falhou de novo) sobe para quem envia, que mostra o erro de
   * rede que já mostrava.
   */
  paraEnvio(): Observable<string> {
    const atual = this.token();
    const valido = atual && this.agora() < this.obtidoEm + this.validadeMs - MARGEM_VENCIMENTO_MS;

    const token$ = valido
      ? of(atual)
      : this.servico.obter().pipe(
          tap((desafio) => this.guardar(desafio)),
          map((desafio) => desafio.desafio),
        );

    return token$.pipe(
      switchMap((token) => {
        const espera = this.obtidoEm + this.idadeMinimaMs + FOLGA_MS - this.agora();

        return espera > 0 ? timer(espera).pipe(map(() => token)) : of(token);
      }),
    );
  }

  private guardar(desafio: DesafioFormulario): void {
    this.obtidoEm = this.agora();
    this.idadeMinimaMs = Math.max(0, desafio.idadeMinimaSegundos ?? 0) * 1000;
    this.validadeMs = Math.max(0, desafio.validadeSegundos ?? 0) * 1000;
    this.token.set(desafio.desafio);
  }

  private esquecerToken(): void {
    this.pedido?.unsubscribe();
    this.pedido = undefined;
    this.token.set(null);
    this.obtidoEm = 0;
  }
}
